import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { GtmService } from '@app-shared/services/analytics/gtm.service';
import { environment } from '../environments/environment';

/**
 * Custom title strategy that formats page titles as "Page Name | BetterSaver"
 * for meaningful Google Analytics page_title tracking.
 *
 * Falls back to the base app title (e.g., "BetterSaver") when no route title is set.
 *
 * The GTM `page_view` is ALSO emitted from here (not from a `NavigationEnd`
 * subscription) so `page_path` and `page_title` always describe the SAME page.
 * The router fires `NavigationEnd` and then calls `updateTitle()` on the very
 * next line (see @angular/router `NavigationTransitions`), so any subscriber
 * reading `document.title` on `NavigationEnd` gets the PREVIOUS page's title —
 * an off-by-one that scrambles every page_title-keyed report (funnels, path
 * exploration, landing pages). Emitting here runs after the title is set and
 * has the resolved URL in hand, keeping the two in lockstep.
 */
@Injectable()
export class PageTitleStrategy extends TitleStrategy {
  private titleService = inject(Title);
  private gtmService = inject(GtmService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const title = this.buildTitle(snapshot);

    if (title) {
      this.titleService.setTitle(`${title} | ${environment.appTitle}`);
    } else {
      this.titleService.setTitle(environment.appTitle);
    }

    // Emit the page_view now that the title is set, pairing it with the URL of
    // the state that produced it. Safe in every environment (GtmService no-ops
    // when gtm.js is not loaded — see GtmService docs).
    this.gtmService.pushTag({
      event: 'page_view',
      page_path: snapshot.url,
      page_title: this.titleService.getTitle(),
    });
  }
}
